import { Content } from '@prismicio/client'
import { SliceComponentProps } from '@prismicio/react'
import React, { useRef } from 'react'
import { isFilled } from '@prismicio/client'
import { gql, useQuery } from '@apollo/client'
import { OutletPresenter, SchedulablePresenter } from 'presenters'
import { Schedulables } from 'shared/presenters/graphqlTypes'
import { Query_Root } from 'shared/presenters/graphqlTypes'
import { ON_SERVER } from 'shared/enums/environment'
import { defined, definedNotNull } from 'shared/presenters/presenter'
import { determineDeploymentEnvironment } from 'shared/helpers/determineEnvironment'
import widgetRoutes from 'enums/routes'
import { useRouter } from 'next/router'
import { SchedulableTypeEnum } from 'shared/enums/schedulables'
import { SOURCE_SUFFIX } from 'enums/marketplace'
import capitalizeFirstLetter from 'shared/helpers/capitalizeFirstLetter'
import { ActivityCarousel } from './ActivityCarousel'
import { ActivityList } from './ActivityList'
import { PrismicRichText } from '@prismicio/react'
import { Text } from 'library/atoms/Typography'

/**
 * Props for `ActivityCarousel`.
 */
export type ActivityCarouselProps =
  SliceComponentProps<Content.ActivityCarouselSlice>

export interface Card {
  href: string
  title: string
  image: string
  address?: string
  price: number
  schedulable: SchedulablePresenter
  outlet: OutletPresenter
}

export type ComponentProps = {
  title: string
  lede?: React.ReactNode
  image?: any
  linkText?: string | null
  linkHref?: string | null
  schedulables?: Card[]
}

const Activities = ({ slice }: ActivityCarouselProps): JSX.Element => {
  const { query } = useRouter()

  const location = isFilled.keyText(slice.primary.location)
    ? slice.primary.location == '{uid}'
      ? capitalizeFirstLetter(query.uid as string)
      : slice.primary.location
    : null
  const locationForSearch = isFilled.keyText(slice.primary.location)
    ? location
    : ''
  const activityTypeForSearch = isFilled.keyText(slice.primary.activity_type)
    ? slice.primary.activity_type
    : ''
  const activityTypeForDisplay = isFilled.keyText(slice.primary.activity_type)
    ? `${slice.primary.activity_type} activities`
    : 'activities'
  const filterToSchedulableSlugs = isFilled.group(
    slice.primary.schedulable_slugs
  )
    ? slice.primary.schedulable_slugs.map(item => item.slug as string)
    : null

  const schedulables = useFetchData(
    activityTypeForSearch,
    location,
    slice.primary.outlet_slug,
    slice.primary.order_by,
    filterToSchedulableSlugs,
    slice.primary.available_to_book_from_days,
    slice.primary.available_to_book_to_days,
    slice.primary.availability_to_book_from_date,
    slice.primary.availability_to_book_to_date
  )

  const href = isFilled.keyText(slice.primary.link_href)
    ? slice.primary.link_href
    : `/search?location=${locationForSearch}&q=${activityTypeForSearch}`
  const hrefText = isFilled.keyText(slice.primary.link_text)
    ? slice.primary.link_text
    : `View all ${activityTypeForDisplay} ${location ? `in ${location}` : ``} on a map`
  const title = isFilled.keyText(slice.primary.title)
    ? slice.primary.title
    : `${capitalizeFirstLetter(activityTypeForDisplay)} in ${location}`
  const lede = isFilled.richText(slice.primary.lede) ? (
    <PrismicRichText
      field={slice.primary.lede}
      components={{
        paragraph: ({ children }) => (
          <Text fontStyle="p2" color="secondary">
            {children}
          </Text>
        )
      }}
    />
  ) : null

  if (slice.variation === 'default') {
    return (
      <ActivityCarousel
        title={title}
        lede={lede}
        linkText={hrefText}
        linkHref={href}
        schedulables={schedulables}
      />
    )
  } else if (slice.variation === 'list') {
    return (
      <ActivityList
        title={title}
        lede={lede}
        image={slice.primary.image}
        linkText={hrefText}
        linkHref={href}
        schedulables={schedulables}
      />
    )
  } else {
    return <></>
  }
}

const useFetchData = (
  schedulableType?: string | null,
  location?: string | null,
  outletSlug?: string | null,
  orderBy?: string | null,
  schedulableSlugs?: string[] | null,
  availableToBookFromDays?: number | null,
  availableToBookToDays?: number | null,
  availabilityToBookFromDate?: string | null,
  availabilityToBookToDate?: string | null
) => {
  const { locale } = useRouter()

  const now = useRef(new Date())

  let toDate = null
  let fromDate = null

  if (availableToBookFromDays) {
    fromDate = now.current
    fromDate.setDate(fromDate.getDate() + availableToBookFromDays)
  } else if (availabilityToBookFromDate) {
    fromDate = new Date(availabilityToBookFromDate)
  }

  if (availableToBookToDays) {
    toDate = now.current
    toDate.setDate(toDate.getDate() + availableToBookToDays)
  } else if (availabilityToBookToDate) {
    toDate = new Date(availabilityToBookToDate)
  }

  const { data } = useQuery<Pick<Query_Root, 'schedulables'>>(
    fetchSchedulablesQuery(
      schedulableType,
      outletSlug,
      location,
      orderBy,
      schedulableSlugs,
      fromDate,
      toDate
    ),
    {
      variables: {
        schedulableType: schedulableType || '',
        locale: locale as string,
        location: `%${location}%`,
        outletSlug: outletSlug || null,
        schedulableSlugs: schedulableSlugs || null,
        fromDate: availableToBookFromDays || null,
        toDate: toDate || null
      },
      skip: ON_SERVER,
      ssr: ON_SERVER
    }
  )

  const formatSchedulables = (schedulablesJSON?: Schedulables[]) => {
    if (!schedulablesJSON) return

    const cards: Card[] = schedulablesJSON.map(schedulableJSON => {
      const schedulable = new SchedulablePresenter(
        definedNotNull(schedulableJSON)
      )
      const outlet = new OutletPresenter(definedNotNull(schedulableJSON).outlet)
      const isActivity = schedulable.type == SchedulableTypeEnum.Activity
      const widgetSlug = defined(outlet?.widgets?.[0].slug)
      const schedulableSlug = defined(schedulable.slug)

      /**
       * Using this method to determine how this link should be rendered in prod.
       * This is a temp fix and should be replaced once Vercel is the main server for eola.co or
       * the search page has been built on Cosmos.
       */
      const prefixHref = determineDeploymentEnvironment().isLive
        ? 'https://widget.eola.co'
        : ''

      const schedulableHref = isActivity
        ? widgetRoutes.ACTIVITY.url({
            widgetSlug,
            activitySlug: schedulableSlug
          })
        : widgetRoutes.RENTAL.url({ widgetSlug, rentalSlug: schedulableSlug })

      return {
        href: prefixHref + schedulableHref + '?' + SOURCE_SUFFIX,
        price: schedulable.priceStartingFrom(),
        title: schedulable.name(),
        image: schedulable.imageURL(),
        address: schedulable.addressSummaryLine(),
        schedulable: schedulable,
        outlet: outlet
      }
    })

    return cards
  }

  return formatSchedulables(data?.schedulables)
}

const fetchSchedulablesQuery = (
  activityType: string | null | undefined,
  outletSlug: string | null | undefined,
  location: string | null | undefined,
  orderBy: string | null | undefined,
  schedulableSlugs: string[] | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined
) => gql`
  query WidgetEolaMarketplaceFetchSchedulables(
    ${activityType ? '$schedulableType: String!' : ''}
    ${location ? '$location: String!' : ''}
    $locale: String!
    ${outletSlug ? '$outletSlug: String!' : ''}
    ${schedulableSlugs && schedulableSlugs?.length > 0 ? '$schedulableSlugs: [String!]!' : ''}
    ${fromDate ? '$fromDate: date!' : ''}
    ${toDate ? '$toDate: date!' : ''}
  ) {
    schedulables(
      where: {
        hide_from_marketplace: { _eq: false }
        materialized_bookability_by_days: {
          hide_from_public: { _eq: false }
          ${fromDate ? `on_date: { _gte: $fromDate }` : ''}
          ${toDate ? ` on_date: { _lte: $toDate }` : ''}
        }
        outlet: {
          admin_forced_marketplace_disable: { _eq: false }
          feature_toggles: { marketplace_enabled: { _eq: true } }
          widgets: { primary: { _eq: true } }
          ${outletSlug ? 'slug: { _eq: $outletSlug }' : ''}
        }
        ${
          location
            ? 'address: { _or: [{ city: { _ilike: $location } }, { state: { _ilike: $location } }] }'
            : ''
        }        
        ${
          activityType
            ? 'activity_type: {_or: [{name: {_ilike: $schedulableType}}, {macro_activity_type_assignments: {macro_activity_type: {name: {_ilike: $schedulableType}}}}]}'
            : ''
        }
        ${schedulableSlugs && schedulableSlugs?.length > 0 ? 'slug: { _in: $schedulableSlugs }' : ''}

        }
      order_by: { ${orderBy === 'Next Availability' ? 'materialized_bookability_by_days_aggregate: {min: {on_date: asc}}' : 'popularity: asc'} }
    ) {
      id
      type
      slug
      image
      price_pence
      child_price_pence
      price_points {
        id
      }
      pricing_model
      resource_requirements {
        resource_price_points(limit: 1) {
          id
        }
      }
      translations(
        where: {
          language: { _in: [$locale, "en"] }
          field: { _in: ["name", "tagline"] }
          model_type: { _eq: "Schedulable" }
        }
      ) {
        language
        field
        value_scrubbed
      }
      default_image {
        id
        image
      }
      address {
        id
        country
        city
      }
      outlet {
        default_currency
        widgets(where: { primary: { _eq: true } }) {
          slug
        }
      }
      materialized_bookability_by_days {
        max_availability
        on_date
      }
      duration_mins
      maximum_capacity
      popularity
      activity_type {
        id
        name
        key_image
        css_class
        abbreviation
      }

    }
  }
`

export default Activities
